import { inject, Injectable } from '@angular/core';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../modals/confirm-dialog/confirm-dialog.component';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDeleteDialogData, DeleteDialogComponent } from '../../modals/delete-dialog/delete-dialog.component';
import { ChoiceDialogComponent } from '../../modals/choice-dialog/choice-dialog.component';
import { ChoiceDialogData, ConfirmDeleteDialogResult } from '../../modals/ConfirmDialogDatas';

@Injectable({ providedIn: 'root' })
export class ModalService {
  private readonly dialog = inject(MatDialog);

  constructor() {
  }

  async switchOrganizationFromSettingsModal(organizationName: string | null | undefined): Promise<boolean> {
    if (!organizationName) return false;

    let confirmed = await firstValueFrom<boolean>(
      this.dialog.open(ConfirmDialogComponent, {
        width: '420px',
        data: {
          title: 'dialogs.switchOrg.title',
          message: 'dialogs.switchOrg.settingsMessage',
          confirmText: 'dialogs.switchOrg.confirm',
          cancelText: 'dialogs.switchOrg.cancel',
          paramMessage: {
            param1: organizationName
          }
        } as ConfirmDialogData
      }).afterClosed()
    );

    return confirmed;
  }

  async switchOrganizationFromShellModal(organizationName: string | null | undefined): Promise<boolean> {
    if (!organizationName) return false;

    let confirmed = await firstValueFrom<boolean>(
      this.dialog.open(ConfirmDialogComponent, {
        width: '420px',
        data: {
          title: 'dialogs.switchOrg.title',
          message: 'dialogs.switchOrg.message',
          confirmText: 'dialogs.switchOrg.confirm',
          cancelText: 'dialogs.switchOrg.cancel',
          paramMessage: {
            param1: organizationName
          }
        } as ConfirmDialogData
      }).afterClosed()
    );

    return confirmed;
  }

  async deleteDatasourceModal(dataSourceName: string | null | undefined): Promise<ConfirmDeleteDialogResult> {
    if (!dataSourceName) return { confirmed: false };

    let confirmed = await firstValueFrom<ConfirmDeleteDialogResult>(
      this.dialog.open(DeleteDialogComponent, {
        width: '420px',
        data: {
          title: 'dialogs.deleteDataSource.title',
          message: 'dialogs.deleteDataSource.message',
          confirmText: 'dialogs.deleteDataSource.confirm',
          cancelText: 'dialogs.deleteDataSource.cancel',
          requiredString: dataSourceName,
          requireName: 'dialogs.deleteDataSource.requireName',
          requireInputLabel: 'dialogs.deleteDataSource.requireInputLabel',
          requireInputError: 'dialogs.deleteDataSource.requireInputError',
          reasonText: 'dialogs.deleteDataSource.reasonText',
          reasonInputLabel: 'dialogs.deleteDataSource.reasonInputLabel',
          reasonInputError: 'dialogs.deleteDataSource.reasonInputError',
          requiredReason: true,
        } as ConfirmDeleteDialogData
      }).afterClosed()
    );

    return confirmed
  }

  async testDatasourceModal(): Promise<string | null> {

    const data: ChoiceDialogData = {
      title: 'dialogs.testDataSource.title',
      message: 'dialogs.testDataSource.message',
      confirmText: 'dialogs.testDataSource.confirm',
      cancelText: 'dialogs.testDataSource.cancel',
      defaultValue: null,

      options: [
        {
          value: 'current',
          label: 'dialogs.testDataSource.currentLabel',
          hint: 'dialogs.testDataSource.currentHint'
        },
        {
          value: 'saved',
          label: 'dialogs.testDataSource.savedLabel',
          hint: 'dialogs.testDataSource.savedHint'
        }
      ]
    };

    let confirmed = await firstValueFrom<string | null>(
      this.dialog.open(ChoiceDialogComponent, {
        width: '420px',
        data: data
      }).afterClosed()
    );

    return confirmed;
  }

  async SaveWithoutTestingDatasource(dataSourceName: string | null | undefined): Promise<boolean> {
    if (!dataSourceName) return false;

    let confirmed = await firstValueFrom<boolean>(
      this.dialog.open(ConfirmDialogComponent, {
        width: '420px',
        data: {
          title: 'dialogs.saveDatasource.withoutTesting.title',
          message: 'dialogs.saveDatasource.withoutTesting.message',
          confirmText: 'dialogs.saveDatasource.withoutTesting.confirm',
          cancelText: 'dialogs.saveDatasource.withoutTesting.cancel',
          paramMessage: {
            param1: dataSourceName
          }
        } as ConfirmDialogData
      }).afterClosed()
    );

    return confirmed;
  }

  async SaveWithFailedTestingDatasource(dataSourceName: string | null | undefined): Promise<boolean> {
    if (!dataSourceName) return false;
    let confirmed = await firstValueFrom<boolean>(
      this.dialog.open(ConfirmDialogComponent, {
        width: '420px',
        data: {
          title: 'dialogs.saveDatasource.failedTesting.title',
          message: 'dialogs.saveDatasource.failedTesting.message',
          confirmText: 'dialogs.saveDatasource.failedTesting.confirm',
          cancelText: 'dialogs.saveDatasource.failedTesting.cancel',
          paramMessage: {
            param1: dataSourceName
          }
        } as ConfirmDialogData
      }).afterClosed()
    );
    return confirmed;
  }
}
